import { Injectable, Pipe, PipeTransform } from '@angular/core';
import { MaxBrainTag } from 'app/library/tag/models/tag.model';

@Pipe({
    name: 'filterTags',
})
@Injectable()
export class FilterTagsPipe implements PipeTransform {
    // NOTE: keep this pipe pure, but allow callers to pass a "layout key" (e.g. loading flag)
    // so the pipe re-runs when the element becomes measurable (offsetWidth changes).
    transform(value: MaxBrainTag[], content: unknown, component: { visualLength: (text: string) => number }, _layoutKey?: unknown): MaxBrainTag[] {
        if (!value) {
            return [];
        }

        const element = this._resolveElement(content);
        if (!element) {
            // Can't measure yet — show nothing (not all tags: that expands the column and
            // makes the next measure think everything fits).
            return [];
        }

        // Prefer a real host width. If the host looks shrink-wrapped (much narrower than its
        // parent — common before chips render in a flex row), use the parent so we don't
        // incorrectly show only "...". Intentionally inset hosts (e.g. card measure strip)
        // stay close to the parent width and keep hostWidth.
        const hostWidth = element.offsetWidth || 0;
        const parentWidth = element.parentElement?.clientWidth || 0;
        const shrinkWrapped = parentWidth > 0 && hostWidth > 0 && hostWidth < parentWidth * 0.5;
        const contentWidth = shrinkWrapped ? parentWidth : hostWidth > 0 ? hostWidth : parentWidth;
        if (contentWidth <= 0) {
            // Not laid out yet — wait for a layout-key remasure; don't expand the cell.
            return [];
        }

        // Heuristic sizing — overestimate chip width so we never paint a half-clipped tag.
        // List chips use padding 0 12px (=24) plus MDC chrome; card chips are slightly tighter.
        const chipExtraWidth = 44;
        const chipGap = 2; // gap between chips / "..."
        // .more-tags is 26px + 4px margin; match CSS max-width: calc(100% - 34px)
        const morePillWidth = 34;

        const tagWidth = (tag: MaxBrainTag): number => {
            // Fallback ~7px/char if the ruler is not ready (0 would make every tag "fit").
            const textWidth = component.visualLength(tag.name) || Math.ceil((tag.name?.length || 0) * 7);
            return textWidth + chipExtraWidth + chipGap;
        };

        // Pass 1: try to fit every tag in one row (no "..." reserve).
        let remainingWidth = contentWidth - 8;
        const allFit = value.every((tag) => {
            const width = tagWidth(tag);
            if (remainingWidth >= width) {
                remainingWidth -= width;
                return true;
            }
            return false;
        });
        if (allFit) {
            return value;
        }

        // Pass 2: reserve space for the "..." pill, then fit as many leading tags as possible.
        remainingWidth = contentWidth - 8 - morePillWidth - chipGap;
        const filteredTags: MaxBrainTag[] = [];
        for (const tag of value) {
            const width = tagWidth(tag);
            if (remainingWidth >= width) {
                remainingWidth -= width;
                filteredTags.push(tag);
            } else {
                break;
            }
        }

        return filteredTags;
    }

    private _resolveElement(content: unknown): HTMLElement | null {
        if (!content) {
            return null;
        }
        if (content instanceof HTMLElement) {
            return content;
        }

        const candidate = content as {
            nativeElement?: unknown;
            elementRef?: { nativeElement?: unknown };
            _elementRef?: { nativeElement?: unknown };
        };

        const natives = [candidate.nativeElement, candidate.elementRef?.nativeElement, candidate._elementRef?.nativeElement];
        for (const native of natives) {
            if (native instanceof HTMLElement) {
                return native;
            }
        }

        return null;
    }
}
